import { useMutation, useQuery } from "@tanstack/react-query";
import { getCompanyId, getCompanyName, getTenantId } from "../utils/cacheStorage";
import { ApiServiceErr, axiosApi } from "./apiService";

export const useGetSubCompany = () =>
  useQuery(
    ["useCompany"],
    async () => {
      const response = await axiosApi.get(`/v2/company/sub-companies/`);
      const [companyId, companyName, tenantId] = await Promise.all([
        getCompanyId(),
        getCompanyName(),
        getTenantId()
      ]);
      const currentCompanyDetails = {
        key: companyId,
        label: companyName,
        value: companyId,
        tenantId: tenantId
      };
      let subCompany = response?.data.reduce((currentType: any, data: any) => {
        let companyDetails = {
          key: data?._id,
          label: data?.name,
          value: data?._id,
          ...data
        };
        return [...currentType, companyDetails];
      }, []);
      return [currentCompanyDetails, ...subCompany];
    },
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetMainProduct = () =>
  useQuery(
    ["useMainProduct"],
    async () => {
      const response = await axiosApi.get(`/v2/products/average-products`);
      let mainProduct = response?.data.reduce((currentType: any, data: any) => {
        let productDetails = {
          key: data?._id,
          label: data?.product_name,
          value: data?._id,
          ...data
        };
        return [...currentType, productDetails];
      }, []);

      return mainProduct;
    },
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetAvailableProduct = (companyId: any) =>
  useQuery(
    ["useAvailableProduct", companyId],
    async () => {
      const response = await axiosApi.get(`/v2/products/${companyId}`);
      let mainProduct = response?.data.reduce((currentType: any, data: any) => {
        let productDetails = {
          key: data?._id,
          ...data
        };
        return [...currentType, productDetails];
      }, []);

      return mainProduct;
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!companyId
    }
  );

export const useGetRootProducts = (rootProductId: any) =>
  useQuery(
    ["useGetRootProducts", rootProductId],
    async () => {
      const response = await axiosApi.get(`/v2/products/link-products/${rootProductId}`);
      let rootProduct = response?.data.reduce((currentType: any, data: any) => {
        let productDetails = {
          key: data?._id,
          productId: data?._id,
          ...data
        };
        return [...currentType, productDetails];
      }, []);

      return rootProduct;
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!rootProductId
    }
  );

export const usePostLinkProduct = () =>
  useMutation<any, ApiServiceErr, any>(async (data: any) => {
    const response = await axiosApi.post(`/v2/products/link-product`, data);
    return response.data;
  });

export const useDeleteLinkProduct = () =>
  useMutation<any, ApiServiceErr, { rootProductId: string; salesUnitId: string | null }>(
    async ({ rootProductId, salesUnitId }) => {
      const response = await axiosApi.delete(`/v2/products/unlink-product`, {
        data: {
          rootProductId,
          salesUnitId
        }
      });
      return response.data;
    }
  );

export const usePatchLinkProductVolume = () =>
  useMutation<any, ApiServiceErr, any>(async (data: any) => {
    const response = await axiosApi.patch(`/v2/products/link-sales-units`, data);
    return response.data;
  });
